import { mat4, vec3 } from 'gl-matrix';

import type { InteractionEvent } from '../../interaction/types';
import type { IViewport } from '../../viewports/types';
import type { ITool } from '../types';
import { FrameCoalescer } from './frame-coalescer';

/** Degrees of rotation per unit of wheel deltaY. */
const WHEEL_ROTATION_SENSITIVITY = 0.1;

/**
 * Vertical rotation tool — rotates the 3D camera around the view-up axis via scroll wheel.
 *
 * Follows the Cornerstone3D VolumeRotateTool approach:
 *   1. Compute rotation angle from wheel delta
 *   2. Build rotation matrix: Translate(focal) × Rotate(angle, viewUp) × Translate(-focal)
 *   3. Transform position and viewUp atomically through the matrix
 */
export class VerticalRotateTool implements ITool {
    static readonly toolName = 'VerticalRotate';

    readonly id = VerticalRotateTool.toolName;
    readonly name = 'Vertical Rotate';
    readonly cursor = 'default';
    readonly completed = false;

    private viewport: IViewport | null = null;
    private pendingDeltaY = 0;
    private coalescer = new FrameCoalescer(() => this.applyPending());

    activate(viewport: IViewport): void {
        this.viewport = viewport;
    }

    deactivate(): void {
        this.coalescer.cancel();
        this.pendingDeltaY = 0;
        this.viewport = null;
    }

    cancel(): void {
        this.coalescer.cancel();
        this.pendingDeltaY = 0;
    }

    handleEvent(event: InteractionEvent): boolean {
        if (!this.viewport) return false;

        if (event.type !== 'wheel' || event.deltaY === undefined) {
            return false;
        }

        this.pendingDeltaY += event.deltaY;
        this.coalescer.schedule();
        return true;
    }

    private applyPending(): void {
        const vp = this.viewport;
        const deltaY = this.pendingDeltaY;
        this.pendingDeltaY = 0;

        if (!vp || deltaY === 0) return;

        const renderer = vp.getRenderer(0);
        const camera = renderer.getActiveCamera();

        const cameraPos = camera.getPosition() as [number, number, number];
        const cameraFp = camera.getFocalPoint() as [number, number, number];
        const cameraViewUp = camera.getViewUp() as [number, number, number];

        // Center of rotation = focal point
        const center = vec3.fromValues(cameraFp[0], cameraFp[1], cameraFp[2]);
        const viewUp = vec3.fromValues(cameraViewUp[0], cameraViewUp[1], cameraViewUp[2]);

        // Rotation angle from wheel delta
        const angle = deltaY * WHEEL_ROTATION_SENSITIVITY * (Math.PI / 180);

        // Build rotation matrix: Translate(center) × Rotate(angle, viewUp) × Translate(-center)
        const transform = mat4.create();
        mat4.translate(transform, transform, center);
        mat4.rotate(transform, transform, angle, viewUp);
        const negCenter = vec3.negate(vec3.create(), center);
        mat4.translate(transform, transform, negCenter);

        // Transform camera position
        const newPos = vec3.fromValues(cameraPos[0], cameraPos[1], cameraPos[2]);
        vec3.transformMat4(newPos, newPos, transform);

        // Transform view-up
        const viewUpPoint = vec3.fromValues(
            cameraPos[0] + cameraViewUp[0],
            cameraPos[1] + cameraViewUp[1],
            cameraPos[2] + cameraViewUp[2]
        );
        vec3.transformMat4(viewUpPoint, viewUpPoint, transform);
        const newViewUp = vec3.sub(vec3.create(), viewUpPoint, newPos);
        vec3.normalize(newViewUp, newViewUp);

        // Apply atomically
        camera.setPosition(newPos[0], newPos[1], newPos[2]);
        camera.setViewUp(newViewUp[0], newViewUp[1], newViewUp[2]);
        camera.orthogonalizeViewUp();

        vp.updateCameraClippingRange();
        vp.render();
    }
}
